/**
 * Step 4's wording: the one-line labels (prototype s4short, phrase), the day a monthly or
 * quarterly schedule sends on, and the "Next sends" list. Step 4's form itself lives in
 * DistributionScheduleStep.model.
 */
import type { Weekday } from "../../utils/scheduleTypes";
import {
  andList,
  formatClockTime,
  formatIsoDateLong,
  formatIsoDateMonthDay,
  formatLongDate,
  formatTimeOfDay,
  pluralize,
  weekdayLongName,
  weekdayShortName,
} from "./distributionFormat";
import { timezoneOption } from "./distributionTime";
import type { DistributionScheduleApi, MonthlyNthApi, SchedulePreviewApi } from "./distributions.types";
import type { NextSendsView } from "./distributionView.types";

const WEEKDAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** App weekday ("mon") → contract weekday (0 = Monday). */
export const toApiWeekday = (day: Weekday): number => WEEKDAYS.indexOf(day);
export const fromApiWeekday = (day: number): Weekday => WEEKDAYS[day] ?? "mon";

const daysLabel = (weekdays: readonly number[], long: boolean): string => {
  const sorted = Array.from(new Set(weekdays)).sort((a, b) => a - b);
  if (sorted.length === 7) return long ? "day" : "Every day";
  if (sorted.length === 5 && sorted.every((d) => d < 5)) return long ? "weekday" : "Weekdays";
  return long ? andList(sorted.map(weekdayLongName)) : sorted.map(weekdayShortName).join(", ");
};

const NTH_LONG: Record<MonthlyNthApi, string> = { 1: "first", 2: "second", 3: "third", 4: "fourth", last: "last" };
const NTH_SHORT: Record<MonthlyNthApi, string> = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", last: "last" };

/**
 * The day a monthly or quarterly schedule sends on, from `monthly_rule` or `day_of_month`:
 * long for sentences ("the first Tuesday", "the last business day", "day 15", "the last day")
 * and short for labels ("1st Tue", "last business day", "day 15", "last day").
 */
export const monthlyDayWords = (
  schedule: Pick<DistributionScheduleApi, "day_of_month" | "monthly_rule">,
): { long: string; short: string } => {
  const rule = schedule.monthly_rule;
  if (rule?.kind === "weekday") {
    return {
      long: `the ${NTH_LONG[rule.nth]} ${weekdayLongName(rule.weekday)}`,
      short: `${NTH_SHORT[rule.nth]} ${weekdayShortName(rule.weekday)}`,
    };
  }
  if (rule?.kind === "business_day") {
    const which = NTH_LONG[rule.nth];
    return { long: `the ${which} business day`, short: `${which} business day` };
  }
  if (schedule.day_of_month === "last") return { long: "the last day", short: "last day" };
  return { long: `day ${schedule.day_of_month ?? 1}`, short: `day ${schedule.day_of_month ?? 1}` };
};

/** "Every Tuesday", "On the last day of every month" (prototype `phrase`). */
export const scheduleRulePhrase = (schedule: DistributionScheduleApi): string => {
  const n = schedule.every;
  if (schedule.kind === "once") return `Once on ${formatIsoDateMonthDay(schedule.starts_on)}`;
  if (schedule.kind === "daily") return n === 1 ? "Every day" : `Every ${n} days`;
  if (schedule.kind === "weekly") {
    const days = daysLabel(schedule.weekdays, true);
    return n === 1 ? `Every ${days}` : `Every ${n} weeks on ${days}`;
  }
  const day = monthlyDayWords(schedule).long;
  if (schedule.kind === "monthly") return `On ${day} of ${n === 1 ? "every month" : `every ${n} months`}`;
  const month = ["first", "second", "third"][(schedule.quarter_month ?? 1) - 1];
  return `On ${day} of the ${month} month of ${n === 1 ? "every quarter" : `every ${n} quarters`}`;
};

/** "Every Tue · 8:00 AM PT" — the rail sublabel and list column (prototype `s4short`). */
export const scheduleShortLabel = (schedule: DistributionScheduleApi | null): string => {
  if (!schedule) return "Send now";
  const time = `${formatClockTime(schedule.at)} ${timezoneOption(schedule.timezone).abbr}`;
  const n = schedule.every;
  if (schedule.kind === "once") return `Once · ${formatIsoDateMonthDay(schedule.starts_on)}, ${time}`;
  if (schedule.kind === "daily") return `${n === 1 ? "Daily" : `Every ${n} days`} · ${time}`;
  if (schedule.kind === "weekly") {
    const days = daysLabel(schedule.weekdays, false);
    const rule = n === 1 ? (days === "Weekdays" || days === "Every day" ? days : `Every ${days}`) : `Every ${n} weeks · ${days}`;
    return `${rule} · ${time}`;
  }
  const day = monthlyDayWords(schedule).short;
  if (schedule.kind === "monthly") return `${n === 1 ? "Monthly" : `Every ${n} months`} · ${day} · ${time}`;
  return `${n === 1 ? "Quarterly" : `Every ${n} quarters`} · ${day} · ${time}`;
};

/** "Ends after 12 sends", "Ends Jan 5", "No end date"; "" for once. */
export const scheduleEndLabel = (schedule: DistributionScheduleApi | null): string => {
  if (!schedule || schedule.kind === "once") return "";
  if (schedule.ends.kind === "never") return "No end date";
  if (schedule.ends.kind === "after") return `Ends after ${pluralize(schedule.ends.runs, "send", "sends")}`;
  return `Ends ${formatIsoDateMonthDay(schedule.ends.date)}`;
};

const relativeDays = (iso: string, nowMs: number, timeZone: string): string => {
  const day = (ms: number) => Date.parse(`${new Date(ms).toLocaleDateString("en-CA", { timeZone })}T00:00:00Z`);
  const n = Math.round((day(Date.parse(iso)) - day(nowMs)) / 86_400_000);
  if (n <= 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n < 14) return `In ${n} days`;
  if (n < 60) return `In ${Math.round(n / 7)} weeks`;
  return `In ${Math.round(n / 30.4)} months`;
};

/** The "Next sends" block from POST /schedule-preview. */
export const toNextSendsView = (
  preview: SchedulePreviewApi,
  schedule: DistributionScheduleApi,
  nowMs: number,
  totalSends: number | null,
): NextSendsView => {
  const zone = timezoneOption(preview.timezone);
  const items = preview.runs.map((iso, i) => {
    const local = new Date(iso).toLocaleDateString("en-CA", { timeZone: preview.timezone });
    const utc = new Date(iso).toISOString().slice(0, 10);
    return {
      n: i + 1,
      dateLabel: formatLongDate(iso, preview.timezone),
      relativeLabel: relativeDays(iso, nowMs, preview.timezone),
      timeLabel: `${formatTimeOfDay(iso, preview.timezone)} ${zone.abbr}`,
      utcLabel: `${formatTimeOfDay(iso, "UTC")} UTC${utc > local ? ", next day" : utc < local ? ", day before" : ""}`,
      note: preview.offset_changes.find((change) => change.at === iso)?.note ?? null,
    };
  });
  let title = "Next 5 sends";
  if (schedule.kind === "once") title = "When it sends";
  else if (totalSends !== null && totalSends <= 5) title = `All ${pluralize(totalSends, "send", "sends")}`;
  else title = `Next ${pluralize(items.length, "send", "sends")}`;
  let footer = "No end date. It runs until you pause or delete it.";
  if (schedule.kind === "once") footer = "Sends once. It doesn’t repeat.";
  else if (schedule.ends.kind === "after" && preview.last_run) footer = `Ends after ${pluralize(totalSends ?? schedule.ends.runs, "send", "sends")} · last send ${formatLongDate(preview.last_run, preview.timezone)}`;
  else if (schedule.ends.kind === "on" && preview.last_run) footer = `Ends ${formatIsoDateLong(schedule.ends.date)} · ${pluralize(totalSends ?? items.length, "send", "sends")} in all · last send ${formatLongDate(preview.last_run, preview.timezone)}`;
  return { title, subtitle: `${zone.short}, with UTC`, items, footer };
};
